import { useEffect, useState } from "react";

import { Box, IconButton, Skeleton, Stack, keyframes } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { PublicityBanner, IPublicityBannerProps } from "@gisce/oficina-virtual-components";
import useLangs from "@/languages/hooks/useLangs";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { fetchCoupons } from "@/actions/coupons";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const CouponsView = () => {
  const dispatch = useAppDispatch();
  const { currentLang } = useLangs();
  const isAuthenticated = useAppSelector(store => store.auth.isAuthenticated);
  const coupons: IPublicityBannerProps["dataProps"][] = useAppSelector(state => state.coupons.data);
  const fetching: boolean = useAppSelector(state => state.coupons.fetching);
  const Settings = useSettingsContext();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCoupons(currentLang));
    }
  }, [isAuthenticated, currentLang]);

  const goToPrevious = () => {
    if (!Array.isArray(coupons) || coupons.length === 0) {
      return;
    }
    setCurrentIndex(prev => (prev === 0 ? coupons.length - 1 : prev - 1));
  };

  const goToNext = () => {
    if (!Array.isArray(coupons) || coupons.length === 0) {
      return;
    }
    setCurrentIndex(prev => (prev === coupons.length - 1 ? 0 : prev + 1));
  };

  useEffect(() => {
    if (
      !isAuthenticated ||
      fetching ||
      !Array.isArray(coupons) ||
      coupons.length === 0
    ) {
      return;
    }
    const interval = setInterval(
      goToNext,
      Settings?.features?.coupons?.carouselInterval ?? 10000
    );
    return () => clearInterval(interval);
  }, [coupons, fetching, isAuthenticated, Settings?.features?.coupons?.carouselInterval]);

  if (fetching || !coupons || !Array.isArray(coupons) || coupons.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        sx={{
          height: { xs: 200, md: 280 },
          width: "100%",
          borderRadius: 3,
        }}
      />
    );
  }

  if (coupons.length === 1) {
    return <PublicityBanner dataProps={coupons[0]} />;
  }

  return (
    <Box sx={{ position: "relative", width: "100%"}}>
      <Box
        key={currentIndex}
        sx={{
          animation: `${fadeIn} 0.4s ease-out`,
          boxShadow: 3,
          borderRadius: 3
        }}
      >
        <PublicityBanner dataProps={coupons[currentIndex]} />
      </Box>
      <IconButton
        onClick={goToPrevious}
        aria-label="Previous coupon"
        sx={{
          position: "absolute",
          left: 32,
          bottom: { xs: 40, md: 60 },
          zIndex: 1,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          boxShadow: 2,
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <ChevronLeft />
      </IconButton>
      <IconButton
        onClick={goToNext}
        aria-label="Next coupon"
        sx={{
          position: "absolute",
          right: 32,
          bottom: { xs: 40, md: 60 },
          transform: "translateY(0)",
          zIndex: 1,
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          boxShadow: 2,
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 1)",
          },
        }}
      >
        <ChevronRight />
      </IconButton>
      <Stack data-testid="carousel-dots" direction="row" spacing={1} justifyContent="center" sx={{ mt: 3, mb: 2 }}>
        {coupons.map((_, idx) => (
          <Box
            key={idx}
            data-testid={`carousel-dot-${idx}`}
            onClick={() => setCurrentIndex(idx)}
            sx={{
              width: idx === currentIndex ? 24 : 8,
              height: 8,
              borderRadius: 1,
              cursor: "pointer",
              backgroundColor: idx === currentIndex ? "primary.main" : "text.secondary",
              transition: "all 0.3s ease",
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};
